// SNIGJ - Admin Widget Configuration Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  LayoutDashboard, Settings, Eye, EyeOff, Palette, GripVertical, Save, 
  RefreshCw, Plus, Trash2, Edit, FileText, Users, Briefcase, CreditCard,
  AlertTriangle, BarChart3, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Default widget configurations
const DEFAULT_WIDGETS = [
  {
    id: 'cases',
    key: 'cases',
    icon: 'FileText',
    title_fr: 'Dossiers',
    title_en: 'Cases',
    description_fr: 'Gestion des dossiers judiciaires',
    description_en: 'Case management',
    color: 'bg-blue-500',
    textColor: 'text-white',
    enabled: true,
    order: 1,
    dataKey: 'cases',
    showOnRoles: ['ALL']
  },
  {
    id: 'prisoners',
    key: 'prisoners',
    icon: 'Users',
    title_fr: 'Détenus',
    title_en: 'Prisoners',
    description_fr: 'Suivi des détenus',
    description_en: 'Prisoner tracking',
    color: 'bg-orange-500',
    textColor: 'text-white',
    enabled: true,
    order: 2,
    dataKey: 'prisoners',
    showOnRoles: ['ALL']
  },
  {
    id: 'payments',
    key: 'payments',
    icon: 'CreditCard',
    title_fr: 'Paiements',
    title_en: 'Payments',
    description_fr: 'Transactions financières',
    description_en: 'Financial transactions',
    color: 'bg-green-500',
    textColor: 'text-white',
    enabled: true,
    order: 3,
    dataKey: 'payments',
    showOnRoles: ['ALL']
  },
  {
    id: 'complaints',
    key: 'complaints',
    icon: 'AlertTriangle',
    title_fr: 'Plaintes',
    title_en: 'Complaints',
    description_fr: 'Gestion des plaintes',
    description_en: 'Complaint management',
    color: 'bg-red-500',
    textColor: 'text-white',
    enabled: true,
    order: 4,
    dataKey: 'complaints',
    showOnRoles: ['ALL']
  }
];

const AVAILABLE_COLORS = [
  { value: 'bg-blue-500', label: 'Bleu', hex: '#3B82F6' },
  { value: 'bg-green-500', label: 'Vert', hex: '#22C55E' },
  { value: 'bg-orange-500', label: 'Orange', hex: '#F97316' },
  { value: 'bg-red-500', label: 'Rouge', hex: '#EF4444' },
  { value: 'bg-purple-500', label: 'Violet', hex: '#A855F7' },
  { value: 'bg-yellow-500', label: 'Jaune', hex: '#EAB308' },
  { value: 'bg-pink-500', label: 'Rose', hex: '#EC4899' },
  { value: 'bg-indigo-500', label: 'Indigo', hex: '#6366F1' },
  { value: 'bg-teal-500', label: 'Turquoise', hex: '#14B8A6' },
  { value: 'bg-gray-500', label: 'Gris', hex: '#6B7280' }
];

const AVAILABLE_ICONS = [
  { value: 'FileText', label: 'Document', component: FileText },
  { value: 'Users', label: 'Utilisateurs', component: Users },
  { value: 'Briefcase', label: 'Mallette', component: Briefcase },
  { value: 'CreditCard', label: 'Carte', component: CreditCard },
  { value: 'AlertTriangle', label: 'Alerte', component: AlertTriangle },
  { value: 'BarChart3', label: 'Graphique', component: BarChart3 },
  { value: 'LayoutDashboard', label: 'Dashboard', component: LayoutDashboard },
  { value: 'Settings', label: 'Paramètres', component: Settings }
];

const AdminWidgetsPage = () => {
  const { language } = useAuth();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/widgets');
      if (response.data?.widgets?.length > 0) {
        setWidgets(response.data.widgets);
      } else {
        // Use default widgets if none configured
        setWidgets(DEFAULT_WIDGETS);
      }
    } catch (error) {
      console.log('Using default widgets');
      setWidgets(DEFAULT_WIDGETS);
    } finally {
      setLoading(false);
    }
  };

  const saveWidgets = async () => {
    try {
      setSaving(true);
      await api.post('/admin/widgets', { widgets });
      toast.success(language === 'fr' ? 'Configuration sauvegardée' : 'Configuration saved');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWidget = (widget) => {
    setEditingWidget({ ...widget });
    setShowEditModal(true);
  };

  const handleSaveWidget = () => {
    if (!editingWidget) return;
    
    setWidgets(prev => prev.map(w => 
      w.id === editingWidget.id ? editingWidget : w
    ));
    setShowEditModal(false);
    setEditingWidget(null);
    toast.success(language === 'fr' ? 'Widget mis à jour' : 'Widget updated');
  };

  const toggleWidgetEnabled = (widgetId) => {
    setWidgets(prev => prev.map(w => 
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    ));
  };

  const moveWidget = (index, direction) => {
    const newWidgets = [...widgets];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newWidgets.length) return;
    
    [newWidgets[index], newWidgets[targetIndex]] = [newWidgets[targetIndex], newWidgets[index]];
    
    // Update order values
    newWidgets.forEach((w, i) => { w.order = i + 1; });
    
    setWidgets(newWidgets);
  };

  const getIconComponent = (iconName) => {
    const iconDef = AVAILABLE_ICONS.find(i => i.value === iconName);
    return iconDef ? iconDef.component : FileText;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-widgets-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <LayoutDashboard className="w-6 h-6" />
              {language === 'fr' ? 'Configuration des Widgets' : 'Widget Configuration'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? "Personnaliser l'apparence et l'ordre des widgets du tableau de bord"
                : 'Customize the appearance and order of dashboard widgets'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {previewMode 
                ? (language === 'fr' ? 'Mode édition' : 'Edit mode')
                : (language === 'fr' ? 'Aperçu' : 'Preview')}
            </Button>
            <Button onClick={saveWidgets} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving 
                ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...')
                : (language === 'fr' ? 'Sauvegarder' : 'Save')}
            </Button>
          </div>
        </div>

        {/* Preview Mode */}
        {previewMode ? (
          <Card>
            <CardHeader>
              <CardTitle>{language === 'fr' ? 'Aperçu des widgets' : 'Widget Preview'}</CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Voici comment les widgets apparaîtront sur le tableau de bord'
                  : 'This is how widgets will appear on the dashboard'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {widgets.filter(w => w.enabled).map((widget) => {
                  const IconComponent = getIconComponent(widget.icon);
                  return (
                    <div 
                      key={widget.id}
                      className={`${widget.color} ${widget.textColor} rounded-lg p-4 shadow-lg`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <IconComponent className="w-8 h-8 opacity-80" />
                        <span className="text-2xl font-bold">--</span>
                      </div>
                      <h3 className="font-semibold">
                        {language === 'fr' ? widget.title_fr : widget.title_en}
                      </h3>
                      <p className="text-sm opacity-80">
                        {language === 'fr' ? widget.description_fr : widget.description_en}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Edit Mode */
          <Card>
            <CardHeader>
              <CardTitle>{language === 'fr' ? 'Liste des widgets' : 'Widget List'}</CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Glissez pour réorganiser, cliquez pour modifier'
                  : 'Drag to reorder, click to edit'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {widgets.map((widget, index) => {
                  const IconComponent = getIconComponent(widget.icon);
                  const colorDef = AVAILABLE_COLORS.find(c => c.value === widget.color);
                  
                  return (
                    <div 
                      key={widget.id}
                      className={`flex items-center gap-4 p-4 border rounded-lg ${
                        !widget.enabled ? 'opacity-50 bg-muted' : ''
                      }`}
                    >
                      {/* Drag Handle */}
                      <div className="flex flex-col gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => moveWidget(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => moveWidget(index, 'down')}
                          disabled={index === widgets.length - 1}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Icon Preview */}
                      <div 
                        className={`w-12 h-12 rounded-lg ${widget.color} ${widget.textColor} flex items-center justify-center`}
                      >
                        <IconComponent className="w-6 h-6" />
                      </div>

                      {/* Widget Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">
                            {language === 'fr' ? widget.title_fr : widget.title_en}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            {widget.key}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? widget.description_fr : widget.description_en}
                        </p>
                      </div>

                      {/* Color Indicator */}
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: colorDef?.hex || '#3B82F6' }}
                        />
                        <span className="text-sm text-muted-foreground">
                          {colorDef?.label || widget.color}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={widget.enabled}
                          onCheckedChange={() => toggleWidgetEnabled(widget.id)}
                        />
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEditWidget(widget)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Widget Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Modifier le widget' : 'Edit Widget'}
              </DialogTitle>
            </DialogHeader>
            
            {editingWidget && (
              <div className="space-y-4">
                <Tabs defaultValue="french">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="french">Français</TabsTrigger>
                    <TabsTrigger value="english">English</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="french" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Titre (FR)</Label>
                      <Input
                        value={editingWidget.title_fr}
                        onChange={(e) => setEditingWidget({
                          ...editingWidget,
                          title_fr: e.target.value
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (FR)</Label>
                      <Textarea
                        value={editingWidget.description_fr}
                        onChange={(e) => setEditingWidget({
                          ...editingWidget,
                          description_fr: e.target.value
                        })}
                        rows={2}
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="english" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title (EN)</Label>
                      <Input
                        value={editingWidget.title_en}
                        onChange={(e) => setEditingWidget({
                          ...editingWidget,
                          title_en: e.target.value
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (EN)</Label>
                      <Textarea
                        value={editingWidget.description_en}
                        onChange={(e) => setEditingWidget({
                          ...editingWidget,
                          description_en: e.target.value
                        })}
                        rows={2}
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Couleur' : 'Color'}</Label>
                    <Select
                      value={editingWidget.color}
                      onValueChange={(value) => setEditingWidget({
                        ...editingWidget,
                        color: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_COLORS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: color.hex }}
                              />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Icône' : 'Icon'}</Label>
                    <Select
                      value={editingWidget.icon}
                      onValueChange={(value) => setEditingWidget({
                        ...editingWidget,
                        icon: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map((icon) => {
                          const IconComp = icon.component;
                          return (
                            <SelectItem key={icon.value} value={icon.value}>
                              <div className="flex items-center gap-2">
                                <IconComp className="w-4 h-4" />
                                {icon.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-4 p-4 border rounded-lg bg-muted/50">
                  <Label className="text-xs text-muted-foreground mb-2 block">
                    {language === 'fr' ? 'Aperçu' : 'Preview'}
                  </Label>
                  <div className={`${editingWidget.color} ${editingWidget.textColor} rounded-lg p-4`}>
                    {(() => {
                      const IconComp = getIconComponent(editingWidget.icon);
                      return (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <IconComp className="w-8 h-8 opacity-80" />
                            <span className="text-2xl font-bold">123</span>
                          </div>
                          <h3 className="font-semibold">
                            {language === 'fr' ? editingWidget.title_fr : editingWidget.title_en}
                          </h3>
                          <p className="text-sm opacity-80">
                            {language === 'fr' ? editingWidget.description_fr : editingWidget.description_en}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleSaveWidget}>
                <Save className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Appliquer' : 'Apply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminWidgetsPage;
